import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, distinct, every, first, map, Subject, takeUntil, tap } from 'rxjs';
import { LgvAPIService, LgvConstants, LgvUserService, NotificationService } from '../../core/core.module';
import { BusinessType } from '../../core/enums/shared';
import { LgvModel } from '../../data-models/longevity.model';
import { environment as env } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { AddressValidationDialogComponent } from '../address-validation-dialog/address-validation-dialog.component';

@Component({
    selector: 'lus-company-edit-modal',
    templateUrl: './company-edit-modal.component.html',
    styleUrls: ['./company-edit-modal.component.scss'],
    //changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanyEditModalComponent implements OnInit, OnDestroy {

    isLoading$ = new BehaviorSubject(false);
    onDestroy$ = new Subject();

    companyForm: UntypedFormGroup;
    //companyIdParam: string = null;

    companyInfoParam: LgvModel.CompanyInfo = null;

    selectedAddressControl = new FormControl(null);
    unitNumberControl = new FormControl('');
    cityControl = new FormControl(null);
    stateControl = new FormControl(null);
    zipControl = new FormControl(null);
    validatedPropertySelected$ = new BehaviorSubject(false);

    selectedProperty = {
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        county: '',
        propertyId: ''
    };


    get isCreateMode(): boolean { return !(!!this.companyInfoParam); }
    get title(): string { return this.isCreateMode ? 'Create Company' : 'Edit Company'; }

    get businessTypeIdCtrl(): AbstractControl { return this.companyForm.controls?.businessTypeId; }
    get lenderPartnerCompanyIdCtrl(): AbstractControl { return this.companyForm.controls?.lenderPartnerCompanyId; }
    get nmlsidCtrl(): AbstractControl { return this.companyForm.controls?.nmlsid; }
    get licenseNumberCtrl(): AbstractControl { return this.companyForm.controls?.licenseNumber; }
    get businessTypeId(): string { return this.businessTypeIdCtrl.value ?? ""; }
    get isRealEstate(): boolean { return this.businessTypeId.toUpperCase() === BusinessType.RealEstate }
    get isMortgageCompany(): boolean { return this.businessTypeId.toUpperCase() === BusinessType.MortgageLender }


    constructor(private fb: UntypedFormBuilder,
        public lgvUserSvc: LgvUserService,
        private route: ActivatedRoute,
        private router: Router,
        private http: HttpClient,
        private dialog: MatDialog,
        public lgvApiSvc: LgvAPIService,
        private notificationSvc: NotificationService,
        public dialogRef: MatDialogRef<CompanyEditModalComponent>,
        private cdr: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) public data: any) {
        this.companyInfoParam = this.data?.companyInfo;
    }


    ngOnInit(): void {
        this.initializeForm();
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    initializeForm() {

        this.companyForm = this.fb.group({
            id: [],
            name: [null, [Validators.required]],
            companyCode: [null],
            businessTypeId: [{ value: null, disabled: !this.isCreateMode }, [Validators.required]],
            businessTypeName: [null, [Validators.required]],
            lenderPartnerCompanyId: [{ value: null, disabled: !this.isCreateMode }],
            lenderPartnerCompanyName: [{ value: null, disabled: !this.isCreateMode }],
            address1: [null, [Validators.required]],
            city: [null, [Validators.required]],
            state: [null, [Validators.required]],
            zipCode: [null, [Validators.required]],
            phone: [null],
            fax: [null],
            website: [null],
            licenseNumber: [null],
            nmlsid: [null],
            companyLogoUrl: [null],
            companyEmail: [null],
            disclaimer: [null],
        });

        if (!!this.companyInfoParam) {
            this.companyForm.patchValue(this.companyInfoParam);
        }

        // Check for business type changes and set the lenderPartner validation accordingly (required only if businessType = Real Estate)
        this.companyForm.controls.businessTypeId.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
            this.updateRequiredFields();
            // Force Validation
            this.companyForm.markAllAsTouched();
        });

        if (!!this.companyInfoParam) {
            this.selectedAddressControl.setValue({
                address: this.companyInfoParam.address1,
                city: this.companyInfoParam.city,
                state: this.companyInfoParam.state,
                zip: this.companyInfoParam.zipCode
            });
            this.cityControl.setValue(this.companyInfoParam.city);
            this.stateControl.setValue(this.companyInfoParam.state);
            this.zipControl.setValue(this.companyInfoParam.zipCode);
        }

        this.updateRequiredFields();
    }

    updateRequiredFields() {
        if (this.isRealEstate) {
            this.lenderPartnerCompanyIdCtrl.setValidators([Validators.required]);
            this.licenseNumberCtrl.setValidators([Validators.required]);
            this.nmlsidCtrl.setValidators([]);
            this.nmlsidCtrl.setValue(null);
        }
        else {
            this.lenderPartnerCompanyIdCtrl.setValidators([]);
            this.lenderPartnerCompanyIdCtrl.setValue(null);
            this.nmlsidCtrl.setValidators([Validators.required]);
            this.licenseNumberCtrl.setValidators([]);
        }
        this.lenderPartnerCompanyIdCtrl.updateValueAndValidity();
        this.licenseNumberCtrl.updateValueAndValidity();
        this.nmlsidCtrl.updateValueAndValidity();
    }

    selectBusinessType(id: string) {
        this.lgvApiSvc.businessTypes$.pipe(takeUntil(this.onDestroy$)).subscribe(x => {
            const obj = x.filter(x => id === x.id);
            this.companyForm.get('businessTypeName').setValue(obj[0].name);
        });
    }

    selectLenderPartner(id: string) {
        this.lgvApiSvc.lenderPartners$.pipe(takeUntil(this.onDestroy$)).subscribe(x => {
            const obj = x.filter(x => id === x.id);
            this.companyForm.get('businessTypeName').setValue(obj[0].name);
        });
    }

    onAddressChanged(event: any) {
        this.companyForm.controls.address1.setValue(event.address);
        this.companyForm.controls.city.setValue(event.city);
        this.companyForm.controls.state.setValue(event.state);
        this.companyForm.controls.zipCode.setValue(event.zip);
    }

    onSubmit() {
        if (!this.companyForm.valid) {
            return;
        }

        let model = this.companyForm.getRawValue();
        //console.log('Model:');
        //console.log(model);

        this.isLoading$.next(true);

        this.lgvApiSvc.SaveCompany(model).pipe(takeUntil(this.onDestroy$)).subscribe(companyResponse => {

            this.isLoading$.next(false);
            setTimeout(() => {
                this.notificationSvc.success('Company has been saved');
            }, 500)
            this.lgvApiSvc.loadCompanies();
            this.dialogRef.close({ action: true, companyResponse });

            if (this.isCreateMode) {
                this.router.navigate([`/admin/company`, companyResponse?.id]);
            }

        }
            , err => {
                this.isLoading$.next(false);
                this.notificationSvc.error("There was an error saving the company");
            }
        );
    }

    onCancel() {
        this.dialogRef.close({ action: false });
    }
}
